<script>
import MixedGrid from '@phoenix/mixins/components/mixedGrid/MixedGrid';
import ProductCard from '@maison/components/product/ProductCard.vue';
import MixedGridFilters from './MixedGridFilters';
import MixedGridImageItem from './MixedGridImageItem';
import MixedGridVideoItem from './MixedGridVideoItem';
import MixedGridPagePushItem from './MixedGridPagePushItem';
import MixedGridReversoItem from './MixedGridReversoItem';
import { map } from 'lodash-es';
import { computed } from 'vue';
import { emitter } from '@phoenix/event-bus';
import { WATCH_COMPARATOR_TOGGLED } from '@maison/helpers/watch-comparator-localstorage-helper';
import { useMixedGridItems } from '@maison/composables/mixedGridItems';

export default {
  components: {
    ProductCard,
    MixedGridFilters,
    MixedGridImageItem,
    MixedGridVideoItem,
    MixedGridPagePushItem,
    MixedGridReversoItem,
  },

  mixins: [MixedGrid],

  provide() {
    return {
      hasFindYourFitWidget: this.hasFindYourFitWidget,
      reversoAllCounts: computed(() => this.reversoAllCounts),
      reversoFilterId: this.reversoFilterId,
    };
  },

  props: {
    allSortedItems: {
      type: Array,
      required: true,
    },
    cta: {
      type: Object,
      default: null,
    },
    isDarkMode: {
      type: Boolean,
      default: false,
    },
    customTags: {
      type: Array,
      default: () => [],
    },
    reversoFilterId: {
      type: String,
      default: null,
    },
    hasFindYourFitWidget: {
      type: Boolean,
      default: false,
    },
    showWatchComparator: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { getComponent } = useMixedGridItems();

    return { getComponent };
  },

  data() {
    return {
      watchComparatorSelectable: false,
    };
  },

  computed: {
    loadMoreClasses() {
      return [
        'products__filtered-buttons',
        map(this.maxPage, (page, size) => {
          const prefix = size !== 'xs' ? `${size}:` : '';
          const hidden = prefix ? 'hidden' : 'force-hidden'; // The 'hidden' class is not correctly imported in the SG right now, remove later
          return this.page >= page ? `${prefix}${hidden}` : `${prefix}grid`;
        }),
      ];
    },

    reversoAllCounts() {
      const activeFilters = { ...this.activeFilters };
      delete activeFilters[this.reversoFilterId];
      return this.filterProducts(this.columns, activeFilters).filter(({ template }) => template === 'product').length;
    },
  },

  methods: {
    initColumns() {
      this.columns = this.allSortedItems.map((item) => {
        if (item.template === 'product') {
          return {
            data: {
              ...item,
              enableSlider: true,
            },
            template: 'product',
            size: 1,
          };
        }
        return item;
      });

      const currentSize = {};
      for (const size of Object.keys(this.widths)) {
        currentSize[size] = 0;
      }

      for (const column of this.columns) {
        column.page = {};

        Object.keys(currentSize).forEach((size) => {
          let columnSize;
          if (['sm', 'xs'].includes(size) && column.template !== 'product') {
            columnSize = 2;
          } else {
            columnSize = column.size;
          }

          currentSize[size] += columnSize;
          column.page[size] = Math.ceil(currentSize[size] / this.itemsPerPage);
        });

        this.maxPage = { ...column.page };
      }
    },

    getDisplayedColumnClasses(column) {
      if (!this.shouldDisplayLoadMore) {
        return [];
      }

      return map(column.page, (page, size) => {
        const prefix = size !== 'xs' ? `${size}:` : '';
        return this.page < page ? `${prefix}hidden` : `${prefix}block`;
      });
    },

    toggleFiltersInMixedGridFilters(filterId, value) {
      this.$refs.mixedGridFilters.toggleFilter(filterId, value);
    },

    onWatchComparatorToggle(value) {
      this.watchComparatorSelectable = value;
      emitter.emit(WATCH_COMPARATOR_TOGGLED, value);
    },
  },
};
</script>

<template>
  <mixed-grid-filters
    v-if="displayFilter"
    ref="mixedGridFilters"
    :filters="filtersWithLocalizedPrices"
    :cta="cta"
    :empty-filters="emptyFilters"
    :number-products-by-filters="numberProductsByFilters"
    :products-count="filteredColumnsCount"
    :unique-id="uniqueId"
    :default-sort="defaultSort"
    :default-filters="activeFilters"
    :should-display-sellable="shouldDisplaySellable"
    :has-filters-in-url="hasFiltersInUrl"
    :is-dark-mode="isDarkMode"
    :unclearable-filters="unclearableFilters"
    :should-display-unclearable-filters="shouldDisplayUnclearableFilters"
    :custom-tags="customTags"
    :show-watch-comparator="showWatchComparator"
    @on-sort-change="onSortChange"
    @on-filters-changed="onFiltersChanged"
    @on-watch-comparator-toggle="onWatchComparatorToggle"
  />
  <div class="container section">
    <div class="products__grid">
      <div class="mixed-grid" data-cy="mixed-grid">
        <component
          :is="getComponent(column.template)"
          v-for="column in filteredColumns"
          :key="column.data.itemId"
          v-bind="column.data"
          :should-display-add-to-watch-comparator="
            column.template === 'product' && column.data.isWatches
              ? showWatchComparator && watchComparatorSelectable
              : false
          "
          :display-classes="getDisplayedColumnClasses(column)"
          data-cy="mixed-grid-item"
          :data-cy-item="column.template"
          :data-tracking="column.template === 'product' ? getDataTracking(column.data) : null"
          @on-toggle-filter="toggleFiltersInMixedGridFilters"
        >
        </component>
      </div>
    </div>
    <div v-if="shouldDisplayLoadMore" :class="loadMoreClasses">
      <button type="button" class="btn btn--default" data-cy="mixed-grid-load-more" @click="nextPage">
        <span class="btn__text">{{ $t('Button.loadMore') }}</span>
      </button>

      <button type="button" class="btn btn--default btn--negative" data-cy="mixed-grid-see-all" @click="seeAll">
        <span class="btn__text">{{ $t('Button.seeAll') }}</span>
      </button>
    </div>
  </div>
</template>
